import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Undo as RollbackIcon,
  Storage as DbIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import config from '../config';
import { useRole } from '../contexts/RoleContext';
import { msalInstance } from '../App';
import { loginRequest } from '../authConfig';

// ─── helpers ────────────────────────────────────────────────────────────────

const getAuthHeaders = async () => {
  const headers = {};
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      headers['User-Email'] = account.username;
      const tokenResponse = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
      headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
    }
  } catch (_) {}
  return headers;
};

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${config.apiUrl}${path}`, {
    ...options,
    headers: { ...await getAuthHeaders(), ...(options.headers || {}) },
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || json.errors?.join(', ') || `HTTP ${res.status}`);
  return json;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';
const plural = (n, w) => `${n} ${w}${n !== 1 ? 's' : ''}`;

// ─── sub-components ─────────────────────────────────────────────────────────

const StatChip = ({ label, count, color, icon }) => (
  <Chip
    icon={icon}
    label={`${label}: ${count}`}
    color={count > 0 ? color : 'default'}
    variant={count > 0 ? 'filled' : 'outlined'}
    size="small"
    sx={{ mr: 1, mb: 1 }}
  />
);

const CollapsibleList = ({ title, items, color = 'text.secondary' }) => {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <Box mt={1}>
      <Button size="small" onClick={() => setOpen(!open)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
        {title} ({items.length})
      </Button>
      <Collapse in={open}>
        <Box sx={{ maxHeight: 200, overflowY: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1, mt: 0.5 }}>
          {items.map((item, i) => (
            <Typography key={i} variant="caption" display="block" color={color}>
              {typeof item === 'string' ? item : item.cir_id}
              {item.changed_fields ? ` — changed: ${item.changed_fields.slice(0, 5).join(', ')}` : ''}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const TEMUploadPage = () => {
  const userRole = useRole();

  const [dbStatus, setDbStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadWarnings, setUploadWarnings] = useState([]);

  const [preview, setPreview] = useState(null);   // { preview_token, diff_summary }
  const [confirming, setConfirming] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(null);

  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fileInputRef = useRef();

  // ── access control ──────────────────────────────────────────────────────
  const isAdmin = userRole.roleName === 'ODM Admin';

  if (!isAdmin) {
    return (
      <Layout>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Alert severity="error" icon={<WarningIcon />}>
            <AlertTitle>Access Denied</AlertTitle>
            This page is restricted to ODM Admins only.
          </Alert>
        </Container>
      </Layout>
    );
  }

  // ── load initial data ───────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await apiFetch('/api/tem/status');
      setDbStatus(data);
    } catch (e) {
      console.error('Failed to fetch TEM status:', e);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await apiFetch('/api/tem/history');
      setHistory(data.history || []);
    } catch (e) {
      console.error('Failed to fetch TEM history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ── file handling ───────────────────────────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setPreview(null);
    setConfirmResult(null);
    setUploadErrors([]);
    setUploadWarnings([]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleGeneratePreview = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadErrors([]);
    setUploadWarnings([]);
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await fetch(`${config.apiUrl}/api/tem/preview`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: formData,
      });
      const json = await res.json();

      if (!res.ok || !json.success) {
        setUploadErrors(json.errors || [json.error || 'Unknown error']);
        return;
      }

      setPreview({ preview_token: json.preview_token, diff_summary: json.diff_summary });
      if (json.warnings?.length) setUploadWarnings(json.warnings);
    } catch (e) {
      setUploadErrors([e.message]);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const data = await apiFetch('/api/tem/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_token: preview.preview_token }),
      });
      setConfirmResult(data.result);
      setPreview(null);
      setSelectedFile(null);
      await fetchStatus();
      await fetchHistory();
    } catch (e) {
      setUploadErrors([e.message]);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    setPreview(null);
    setSelectedFile(null);
    setUploadErrors([]);
    setUploadWarnings([]);
  };

  const handleRollback = async (historyId, filename) => {
    if (!window.confirm(`Rollback to the state BEFORE "${filename}" was uploaded?\n\nThis will restore the TEM inventory to that point.`)) return;
    setRollbackLoading(historyId);
    try {
      await apiFetch(`/api/tem/rollback/${historyId}`, { method: 'POST' });
      await fetchStatus();
      await fetchHistory();
    } catch (e) {
      alert(`Rollback failed: ${e.message}`);
    } finally {
      setRollbackLoading(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/tem/export`, {
        headers: await getAuthHeaders(),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `TEM_export_${Date.now()}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleSeedFromExcel = async () => {
    if (!window.confirm('Seed TEM inventory from the existing Excel file on disk?\nThis should only be done once to migrate from Excel to database storage.')) return;
    setSeeding(true);
    try {
      const data = await apiFetch('/api/tem/seed-from-excel', { method: 'POST' });
      alert(`Seeded successfully: ${data.result?.total_rows || 0} rows imported.`);
      await fetchStatus();
      await fetchHistory();
    } catch (e) {
      alert(`Seed failed: ${e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  // ─── render ──────────────────────────────────────────────────────────────
  const diff = preview?.diff_summary;

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <DbIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>TEM Inventory Upload</Typography>
            <Typography variant="body2" color="text.secondary">
              Admin-only · Upload and merge TEM Excel files into the database
            </Typography>
          </Box>
        </Box>

        {/* DB Status Banner */}
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {loadingStatus ? (
              <LinearProgress />
            ) : dbStatus ? (
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Chip
                  icon={<DbIcon />}
                  label={dbStatus.has_data ? `DB: ${dbStatus.total_records.toLocaleString()} records` : 'DB: Empty'}
                  color={dbStatus.has_data ? 'success' : 'warning'}
                  variant="outlined"
                />
                {dbStatus.last_upload && (
                  <Typography variant="body2" color="text.secondary">
                    Last upload: <strong>{dbStatus.last_upload.filename}</strong> on {fmtDate(dbStatus.last_upload.uploaded_at)} by {dbStatus.last_upload.uploaded_by}
                  </Typography>
                )}
                {dbStatus.has_data && (
                  <Tooltip title="Download the current TEM data as an Excel file — modify it and re-upload to test changes">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting}
                        sx={{ ml: 'auto' }}
                      >
                        {exporting ? 'Exporting…' : 'Download Current TEM Data'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {!dbStatus.has_data && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={seeding ? <CircularProgress size={14} /> : <DbIcon />}
                    onClick={handleSeedFromExcel}
                    disabled={seeding}
                  >
                    {seeding ? 'Seeding…' : 'Seed from Excel file'}
                  </Button>
                )}
              </Box>
            ) : null}
          </CardContent>
        </Card>

        {/* Success banner after confirm */}
        {confirmResult && (
          <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }} onClose={() => setConfirmResult(null)}>
            <AlertTitle>Upload Successful</AlertTitle>
            {plural(confirmResult.rows_added, 'row')} added · {plural(confirmResult.rows_updated, 'row')} updated · {plural(confirmResult.rows_deleted, 'row')} deleted · {confirmResult.total_rows.toLocaleString()} total records now in DB.
          </Alert>
        )}

        {/* Upload Section */}
        {!preview && (
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Upload New TEM File</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Upload the new TEM Excel file. The system will merge by <strong>Cir ID</strong> (column A).
                Columns <strong>CM–CW</strong> (SDWAN data) will be preserved from existing records for matching Cir IDs.
              </Typography>

              {/* Drop Zone */}
              <Box
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : selectedFile ? 'success.main' : 'grey.400',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: dragOver ? 'primary.50' : selectedFile ? 'success.50' : 'grey.50',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsb"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
                {selectedFile ? (
                  <>
                    <CheckIcon color="success" sx={{ fontSize: 40 }} />
                    <Typography variant="subtitle1" color="success.main" fontWeight={600}>{selectedFile.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · Click to change file
                    </Typography>
                  </>
                ) : (
                  <>
                    <UploadIcon color="action" sx={{ fontSize: 40 }} />
                    <Typography variant="subtitle1">Drop TEM Excel file here or click to browse</Typography>
                    <Typography variant="body2" color="text.secondary">Supported: .xlsx, .xls, .xlsb</Typography>
                  </>
                )}
              </Box>

              {uploadErrors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Validation Errors</AlertTitle>
                  {uploadErrors.map((e, i) => <Typography key={i} variant="body2">• {e}</Typography>)}
                </Alert>
              )}

              {uploadWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {uploadWarnings.map((w, i) => <Typography key={i} variant="body2">• {w}</Typography>)}
                </Alert>
              )}

              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                  onClick={handleGeneratePreview}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? 'Analysing…' : 'Generate Preview'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Preview Section */}
        {preview && diff && (
          <Card elevation={3} sx={{ mb: 3, border: '1px solid', borderColor: 'warning.light' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Merge Preview — Review Before Confirming</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                File: <strong>{selectedFile?.name}</strong> · {diff.total_in_upload.toLocaleString()} valid rows
              </Typography>

              {/* Stats */}
              <Box mb={2}>
                <StatChip label="New rows" count={diff.added} color="success" icon={<AddIcon />} />
                <StatChip label="Updated rows" count={diff.updated} color="info" icon={<EditIcon />} />
                <StatChip label="Unchanged" count={diff.unchanged} color="default" />
                <StatChip label="Deleted from DB" count={diff.deleted} color="error" icon={<DeleteIcon />} />
              </Box>

              {/* Deleted warning */}
              {diff.deleted > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Warning: {plural(diff.deleted, 'circuit')} will be removed from the inventory</AlertTitle>
                  These Cir IDs are in the current DB but are missing from the uploaded file. They will be deleted.
                  <CollapsibleList title="View deleted Cir IDs" items={diff.deleted_detail} color="error.main" />
                </Alert>
              )}

              {/* Detail expanders */}
              <CollapsibleList title="New Cir IDs being added" items={diff.added_detail} color="success.main" />
              <CollapsibleList title="Cir IDs with updated fields" items={diff.updated_detail} />

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming ? 'Applying…' : 'Confirm & Apply Merge'}
                </Button>
                <Button variant="outlined" color="error" onClick={handleCancel} disabled={confirming}>
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Upload History */}
        <Card elevation={2}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HistoryIcon color="action" />
              <Typography variant="h6">Upload History</Typography>
              <Chip label={`${history.length} uploads`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
            </Box>

            {loadingHistory ? (
              <LinearProgress />
            ) : history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No uploads yet.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Filename</strong></TableCell>
                      <TableCell><strong>Uploaded By</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="center"><strong>Added</strong></TableCell>
                      <TableCell align="center"><strong>Updated</strong></TableCell>
                      <TableCell align="center"><strong>Deleted</strong></TableCell>
                      <TableCell align="center"><strong>Total</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Rollback</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id} hover sx={{ opacity: h.status === 'rolled_back' ? 0.6 : 1 }}>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={h.filename}><span>{h.filename}</span></Tooltip>
                        </TableCell>
                        <TableCell>{h.uploaded_by}</TableCell>
                        <TableCell>{fmtDate(h.uploaded_at)}</TableCell>
                        <TableCell align="center">
                          <Typography color="success.main" fontWeight={600}>{h.rows_added}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography color="info.main" fontWeight={600}>{h.rows_updated}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography color={h.rows_deleted > 0 ? 'error.main' : 'text.secondary'} fontWeight={600}>
                            {h.rows_deleted}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{h.total_rows_after?.toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={h.status}
                            size="small"
                            color={h.status === 'active' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {h.status === 'active' && (
                            <Tooltip title="Restore inventory to state before this upload">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleRollback(h.id, h.filename)}
                                  disabled={rollbackLoading === h.id}
                                >
                                  {rollbackLoading === h.id
                                    ? <CircularProgress size={16} />
                                    : <RollbackIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default TEMUploadPage;
